import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    CCard,
    CCardBody,
    CCardHeader,
    CForm,
    CFormInput,
    CFormLabel,
    CFormSelect,
    CButton,
    CRow,
    CCol,
    CAlert,
    CFormTextarea,
} from '@coreui/react-pro';
import { useStore } from '../../contexts/StoreContext';
import { Department, LocationData, Municipality } from '../../types/location';
import * as DomicilappService from "../../services/DomicilappService";
import { CategoryItem } from '../../types/category';
import { useNotification } from '../../contexts/NotificationContext';

// Forma del payload que espera el backend para crear una tienda/sucursal.
// Ajusta los nombres de campo si tu API espera otros.
interface CreateStorePayload {
    name: string;
    categories: [string];
    description: string;
    address: string;
    plan_id: number;
    nit: string;
    municipalities_id: number | null;
}

export const StoreCreatePage: React.FC = () => {
    const [formData, setFormData] = useState({
        name: '',
        category: '',
        phone: '',
        nit: '',
        address: '',
        plan_id: 2,
        description: '',
    });

    const { storeId, refreshStores } = useStore();

    const [locations, setLocations] = useState<LocationData[]>([]);
    const [categories, setCategories] = useState<CategoryItem[]>([]);

    const [departments, setDepartments] = useState<Department[]>([]);
    const [selectedDepartmentId, setSelectedDepartmentId] = useState<number | null>(null);
    const [municipalities, setMunicipalities] = useState<Municipality[]>([]);
    const [selectedMunicipalityId, setSelectedMunicipalityId] = useState<number | null>(null);

    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState<string | null>(null);
    const { showModal, showToast } = useNotification();
    const navigate = useNavigate();

    // Carga inicial de ubicaciones y categorías
    useEffect(() => {
        if (!storeId) return;

        setLocations([]);

        DomicilappService.getLocations().then((response) => {
            if (response.status === 200) {
                setLocations(response.data);
            }
        });

        DomicilappService.getAllStoresCategories().then((response) => {
            if (response.status === 200) {
                setCategories(response.data.items);
            }
        });
    }, [storeId]);

    // El listado de tiendas y su recarga ahora viven en StoreContext
    // (refreshStores), compartidos con el Sidebar y cualquier otro
    // consumidor — así no hay que duplicar esta lógica aquí.

    // Selecciona el municipio activo cuando solo hay uno disponible para
    // el departamento elegido.
    const handleDepartmentChange = useCallback((id: string) => {
        const depId = Number(id);

        if (!depId) {
            setSelectedDepartmentId(null);
            setSelectedMunicipalityId(null);
            setMunicipalities([]);
            return;
        }

        setSelectedDepartmentId(depId);
        setSelectedMunicipalityId(null);

        const selectedDep = departments.find((dep) => dep.id === depId);
        if (!selectedDep) {
            setMunicipalities([]);
            return;
        }

        const activeMunicipalities = selectedDep.municipalities.filter(
            (m) => m.municipalities_statuses_id === 1
        );
        setMunicipalities(activeMunicipalities);

        // Si solo hay un municipio activo para este departamento, se preselecciona
        if (activeMunicipalities.length === 1) {
            setSelectedMunicipalityId(activeMunicipalities[0].id);
        }
    }, [departments]);

    // Calcula los departamentos activos a partir de las ubicaciones cargadas.
    // Si solo hay uno, se preselecciona automáticamente (lo que dispara
    // el useEffect de abajo y, en cascada, la preselección del municipio).
    useEffect(() => {
        if (!storeId || locations.length === 0) return;

        const activeDepartments = locations
            .flatMap((location) => location.departments)
            .filter((dep) => dep.departments_statuses_id === 1);

        setDepartments(activeDepartments);

        if (activeDepartments.length === 1) {
            setSelectedDepartmentId(activeDepartments[0].id);
        }
    }, [storeId, locations]);

    // Cuando ya conocemos los departamentos activos y hay un departamento
    // seleccionado (manual o automáticamente), resolvemos sus municipios.
    useEffect(() => {
        if (selectedDepartmentId && departments.length > 0) {
            handleDepartmentChange(selectedDepartmentId.toString());
        }
        // handleDepartmentChange ya está memoizado con useCallback([departments]),
        // así que no hace falta incluirlo aquí para evitar loops innecesarios.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedDepartmentId, departments]);

    // -------------------------------------------------------------------
    // Construcción del payload y llamada a la API, separadas a propósito:
    // buildStorePayload arma los datos; createStore SOLO llama al servicio.
    // -------------------------------------------------------------------
    const buildStorePayload = (): CreateStorePayload => ({
        name: formData.name,
        categories: [formData.category],
        description: formData.description,
        address: formData.address,
        plan_id: 2,
        nit: formData.nit,
        municipalities_id: selectedMunicipalityId,
    });

    // Esta función no arma nada: recibe el payload ya construido y solo
    // hace la llamada a la API.
    const createStore = async (payload: CreateStorePayload) => {
        return DomicilappService.createStore(payload);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSubmitError(null);

        const payload = buildStorePayload();

        try {
            setSubmitting(true);
            const response = await createStore(payload);

            if (response.status === 200 || response.status === 201) {
                showToast({
                    title: '¡Operación Exitosa!',
                    message: 'La tienda se creó de manera exitosa',
                    type: 'success', // 'success' | 'danger' | 'warning' | 'info' | 'brand'
                    delay: 3500,
                });

                // Recarga el listado compartido y selecciona la tienda
                // recién creada ANTES de navegar, para que /store/profile
                // ya la encuentre activa en el contexto (y el Sidebar
                // también, porque lee del mismo StoreContext).
                await refreshStores(response.data.id);
                navigate('/store/profile');
            } else {
                setSubmitError('No se pudo crear la tienda. Intenta de nuevo.');
            }
        } catch (error) {
            setSubmitError('Ocurrió un error al crear la tienda.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <CCard className="border border-secondary-subtle shadow-none mb-4 bg-body rounded-3">
            <CCardHeader className="bg-body border-bottom border-secondary-subtle py-3 text-body">
                <h6 className="mb-0 fw-bold">Registrar Nueva Tienda / Sucursal</h6>
            </CCardHeader>
            <CCardBody className="p-4 bg-body text-body">
                <CForm onSubmit={handleSubmit}>
                    <CAlert color="secondary" className="mb-4 border border-secondary-subtle text-body-secondary bg-body-tertiary small">
                        Ingresa la información básica y comercial del nuevo establecimiento para habilitar su operación dentro de la plataforma.
                    </CAlert>

                    {/* Información General */}
                    <span className="fw-semibold mb-3 text-uppercase text-body-secondary d-block small">Información del Negocio</span>
                    <CRow className="g-3 mb-4">
                        <CCol md={4}>
                            <CFormLabel className="small fw-semibold text-body-secondary mb-1">NIT (Opcional)</CFormLabel>
                            <CFormInput
                                className="bg-body text-body border-secondary-subtle"
                                placeholder="Ej. 900123456-1"
                                value={formData.nit}
                                onChange={(e) => setFormData({ ...formData, nit: e.target.value })}
                            />
                        </CCol>
                        <CCol md={4}>
                            <CFormLabel className="small fw-semibold text-body-secondary mb-1">Nombre Comercial del Comercio</CFormLabel>
                            <CFormInput
                                className="bg-body text-body border-secondary-subtle"
                                placeholder="Ej. Hamburguesas El Corral - Sede Norte"
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            />
                        </CCol>
                        <CCol md={4}>
                            <CFormLabel className="small fw-semibold text-body-secondary mb-1">Categoría Principal</CFormLabel>
                            <CFormSelect
                                className="bg-body text-body border-secondary-subtle"
                                value={formData.category}
                                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                            >
                                <option value="">Seleccione una categoría</option>
                                {categories.map((category) => (
                                    <option key={category.id} value={category.id}>
                                        {category.name}
                                    </option>
                                ))}
                            </CFormSelect>
                        </CCol>

                        <CCol md={12}>
                            <CFormLabel className="small fw-semibold text-body-secondary mb-1">Descripción / Slogan</CFormLabel>
                            <CFormTextarea
                                className="bg-body text-body border-secondary-subtle"
                                placeholder="Ej. Hamburguesas El Corral - Sede Norte"
                                value={formData.description}
                                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                            />
                        </CCol>
                    </CRow>

                    {/* Ubicación y Contacto */}
                    <span className="fw-semibold mb-3 text-uppercase text-body-secondary d-block small">Ubicación y Despacho</span>
                    <CRow className="g-3 mb-4">
                        <CCol md={4}>
                            <CFormLabel className="small fw-semibold text-body-secondary mb-1">Departamento</CFormLabel>
                            <CFormSelect
                                className="bg-body text-body border-secondary-subtle"
                                id="department-select"
                                value={selectedDepartmentId ?? ''}
                                onChange={(e) => handleDepartmentChange(e.target.value)}
                                disabled={departments.length === 1}
                            >
                                <option value="">Seleccione un departamento</option>
                                {departments.map((dep) => (
                                    <option key={dep.id} value={dep.id}>
                                        {dep.name}
                                    </option>
                                ))}
                            </CFormSelect>
                        </CCol>
                        <CCol md={4}>
                            <CFormLabel className="small fw-semibold text-body-secondary mb-1">Municipio</CFormLabel>
                            <CFormSelect
                                className="bg-body text-body border-secondary-subtle"
                                value={selectedMunicipalityId ?? ''}
                                onChange={(e) => setSelectedMunicipalityId(Number(e.target.value))}
                                disabled={!selectedDepartmentId || municipalities.length === 0 || municipalities.length === 1}
                            >
                                <option value="">Seleccione un municipio</option>
                                {municipalities.map((m) => (
                                    <option key={m.id} value={m.id}>
                                        {m.name}
                                    </option>
                                ))}
                            </CFormSelect>
                        </CCol>
                        <CCol md={4}>
                            <CFormLabel className="small fw-semibold text-body-secondary mb-1">Dirección Física del Local</CFormLabel>
                            <CFormInput
                                className="bg-body text-body border-secondary-subtle"
                                placeholder="Ej. Calle 127 #15-45, Local 102"
                                value={formData.address}
                                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                            />
                        </CCol>
                    </CRow>

                    {submitError && (
                        <CAlert color="danger" className="small mb-3">
                            {submitError}
                        </CAlert>
                    )}

                    <CAlert color="warning" className="small mb-3">
                        La creación de la tienda está sujeta a revisión interna antes de habilitarse en la plataforma.
                    </CAlert>

                    <div className="d-flex justify-content-end">
                        <CButton type="submit" color="dark" className="px-4 fw-medium text-white" disabled={submitting}>
                            {submitting ? 'Creando...' : 'Crear'}
                        </CButton>
                    </div>
                </CForm>
            </CCardBody>
        </CCard>
    );
};

export default StoreCreatePage;